import { useState, useCallback } from 'react'
import { QUERY_ARTISTS_ENDPOINT } from '@/config/endpoints'
import type { Artist } from '@/types/models'
import { useAuthState } from './useAuth'
import { useArtistsCache } from '@/contexts/ArtistsCacheContext'

interface QueryParams {
  /** OR semantics; omit or empty = no geographic filter */
  cities?: string[]
  sorting?: 'discover' | 'trending' | 'newest'
  limit?: number
  followingOnly?: boolean
  /** After following-only returns zero rows, fetch discover (same geo/limit) */
  fetchDiscoverFallbackWhenEmpty?: boolean
}

export function useArtistQuery(cacheKey?: string) {
  const { user } = useAuthState()
  const artistsCache = useArtistsCache()

  const cachedEntry = cacheKey ? artistsCache.getEntry(cacheKey) : null

  const [queryResults, setQueryResults] = useState<Artist[]>(() => cachedEntry?.artists ?? [])
  const [discoverFallbackResults, setDiscoverFallbackResults] = useState<Artist[]>([])
  const [isQuerying, setIsQuerying] = useState(false)
  const [queryError, setQueryError] = useState<string | null>(null)
  const [initialLoadComplete, setInitialLoadComplete] = useState(() => !!cachedEntry)

  const performQuery = useCallback(
    async (params: QueryParams) => {
      const compoundFollowing = Boolean(params.followingOnly && params.fetchDiscoverFallbackWhenEmpty)
      const skipCompoundCache = Boolean(cacheKey && compoundFollowing)

      setDiscoverFallbackResults([])

      if (cacheKey && !skipCompoundCache) {
        const freshEntry = artistsCache.getEntry(cacheKey)
        if (freshEntry) {
          setQueryResults(freshEntry.artists)
          setInitialLoadComplete(true)
          return
        }
      }

      setIsQuerying(true)
      setQueryError(null)

      try {
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        }

        if (user) {
          const idToken = await user.getIdToken()
          headers['Authorization'] = `Bearer ${idToken}`
        }

        const geo = params.cities?.filter((c) => c.trim())

        const buildBody = (
          overrides: Partial<{ sorting: string; followingOnly: boolean }>,
        ): Record<string, unknown> => {
          const sorting = overrides.sorting ?? params.sorting ?? 'discover'
          const followingOnly = overrides.followingOnly ?? params.followingOnly ?? false
          const limit = params.limit ?? 50
          const body: Record<string, unknown> = {
            sorting,
            limit,
            followingOnly,
          }
          if (geo?.length) {
            body.cities = geo
          }
          return body
        }

        const fetchOnce = async (body: Record<string, unknown>): Promise<Artist[]> => {
          console.log('🚀 [useArtistQuery] Sending request:', body)

          const response = await fetch(QUERY_ARTISTS_ENDPOINT, {
            method: 'POST',
            headers,
            body: JSON.stringify(body),
          })

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}))
            throw new Error(errorData.error || `Query failed with status ${response.status}`)
          }

          const data = await response.json()
          const artists: Artist[] = data.results || []

          console.group('✅ [useArtistQuery] Response Received')
          console.log(`Status: OK, Artists returned: ${artists.length}`)
          console.log('isAvailable breakdown:', {
            available: artists.filter((a) => a.isAvailable === true).length,
            unavailable: artists.filter((a) => a.isAvailable === false).length,
            undefined: artists.filter((a) => a.isAvailable === undefined).length,
          })
          console.log('Full response data:', data)
          console.groupEnd()

          return artists
        }

        if (compoundFollowing) {
          const primary = await fetchOnce(buildBody({ followingOnly: true }))
          setQueryResults(primary)
          if (primary.length === 0) {
            const fb = await fetchOnce(buildBody({ followingOnly: false, sorting: 'discover' }))
            setDiscoverFallbackResults(fb)
          }
          // Compound responses are not cached: cacheKey can't represent primary+fallback.
        } else {
          const artists = await fetchOnce(buildBody({}))
          setQueryResults(artists)

          if (cacheKey) {
            artistsCache.setEntry(cacheKey, {
              artists,
              cachedAt: Date.now(),
              scrollPosition: 0,
            })
          }
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Query failed'
        setQueryError(errorMessage)
        console.error('[useArtistQuery] Error:', errorMessage)
      } finally {
        setIsQuerying(false)
        setInitialLoadComplete(true)
      }
    },
    [user, cacheKey, artistsCache],
  )

  const clearQuery = useCallback(() => {
    setQueryResults([])
    setDiscoverFallbackResults([])
    setQueryError(null)
    setInitialLoadComplete(false)
  }, [])

  return {
    queryResults,
    discoverFallbackResults,
    isQuerying,
    queryError,
    initialLoadComplete,
    performQuery,
    clearQuery,
  }
}
